import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk' }}>
              Privacy Policy
            </CardTitle>
            <p className="text-sm text-gray-600">Last Updated: {new Date().toLocaleDateString()}</p>
          </CardHeader>
          <CardContent className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>1. Introduction</h2>
              <p>NextGen Trades Academy ("we", "us", or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>2. Information We Collect</h2>
              <p><strong>Personal Information:</strong></p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Name and email address</li>
                <li>Role (student, graduate, or admin)</li>
                <li>Trade focus and specialization</li>
                <li>County of residence</li>
                <li>Graduation year (if applicable)</li>
                <li>Profile information (bio, contact preferences)</li>
                <li>Messages and communications with other users</li>
              </ul>
              <p className="mt-3"><strong>Usage Data:</strong></p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Login times and frequency</li>
                <li>Pages visited and features used</li>
                <li>Device and browser information</li>
                <li>IP address</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>3. How We Use Your Information</h2>
              <p>We use collected information to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Provide and maintain our platform</li>
                <li>Enable networking and communication between users</li>
                <li>Connect students and graduates with job opportunities</li>
                <li>Notify you about events and opportunities in your area</li>
                <li>Improve our services and user experience</li>
                <li>Ensure platform security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>4. Information Sharing</h2>
              <p>We do not sell your personal information. We may share your information:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Within the platform:</strong> Your profile information is visible to other registered users to facilitate networking</li>
                <li><strong>With employers:</strong> Only if you apply to a job posting</li>
                <li><strong>For legal compliance:</strong> When required by law or to protect rights and safety</li>
                <li><strong>Service providers:</strong> Third parties who assist in operating our platform (under confidentiality agreements)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>5. California Privacy Rights (CCPA)</h2>
              <p>California residents have the right to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Know:</strong> What personal information we collect, use, disclose, and sell</li>
                <li><strong>Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-out:</strong> Opt-out of the sale of personal information (we do not sell your information)</li>
                <li><strong>Non-discrimination:</strong> Not be discriminated against for exercising your privacy rights</li>
              </ul>
              <p className="mt-3">To exercise these rights, contact us at: <a href="mailto:privacy@ngta.com" className="text-teal-600 underline">privacy@ngta.com</a></p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>6. Data Security</h2>
              <p>We implement reasonable security measures to protect your information, including:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Encrypted password storage</li>
                <li>Secure authentication tokens</li>
                <li>Regular security assessments</li>
                <li>Access controls and user permissions</li>
              </ul>
              <p className="mt-2">However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>7. Data Retention</h2>
              <p>We retain your personal information for as long as your account is active or as needed to provide services. You may request deletion of your account and data at any time by contacting us.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>8. Third-Party Links</h2>
              <p>Our platform may contain links to third-party websites or services (such as employer websites). We are not responsible for the privacy practices of these third parties. Please review their privacy policies before providing any information.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>9. Children's Privacy</h2>
              <p>Our platform is intended for individuals 16 years of age and older. We do not knowingly collect information from children under 16. If we become aware that we have collected information from a child under 16, we will take steps to delete such information.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>10. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>11. Contact Us</h2>
              <p>For questions about this Privacy Policy or to exercise your privacy rights:</p>
              <ul className="mt-2 space-y-1">
                <li>Email: <a href="mailto:privacy@ngta.com" className="text-teal-600 underline">privacy@ngta.com</a></li>
                <li>Data Deletion Requests: <a href="mailto:privacy@ngta.com" className="text-teal-600 underline">privacy@ngta.com</a></li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}